import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";

export default function PaymentSuccess() {
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [returnRoute, setReturnRoute] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) { setState("error"); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setState("paid");
          api.post("/analytics/event", { event: "checkout_completed" }).catch(() => {});
          const back = localStorage.getItem("mmt_upgrade_return");
          if (back) {
            localStorage.removeItem("mmt_upgrade_return");
            setReturnRoute(back);
            setTimeout(() => navigate(back), 1800);
          }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* keep trying */ }
      tries += 1;
      if (tries >= 8) { setState("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="flex min-h-[70vh] items-center justify-center p-6" data-testid="payment-success-page">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center">
        {state === "checking" && <><Loader2 size={40} className="mx-auto animate-spin text-[#1a5fd0]" /><h1 className="mt-4 font-head text-xl font-extrabold text-slate-900">Confirming your payment…</h1><p className="mt-1 text-[13px] text-slate-500">One moment while we activate your membership.</p></>}
        {state === "paid" && <><span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600"><CheckCircle2 size={28} /></span><h1 className="mt-4 font-head text-xl font-extrabold text-slate-900">You're all set!</h1><p className="mt-1 text-[13px] text-slate-500">{returnRoute ? "Your new features are now unlocked. Taking you back…" : "Your membership is active. Welcome aboard."}</p><button onClick={() => navigate(returnRoute || "/billing")} data-testid="success-continue" className="mt-5 rounded-xl bg-[#1a5fd0] px-5 py-2.5 font-head text-[14px] font-bold text-white hover:bg-[#1550b8]">{returnRoute ? "Continue" : "Go to my membership"}</button></>}
        {state === "pending" && <><Loader2 size={40} className="mx-auto animate-spin text-amber-500" /><h1 className="mt-4 font-head text-xl font-extrabold text-slate-900">Almost there</h1><p className="mt-1 text-[13px] text-slate-500">Your payment is processing. It may take a moment to reflect — you can safely return to your account.</p><button onClick={() => navigate("/billing")} className="mt-5 rounded-xl bg-[#080d1a] px-5 py-2.5 font-head text-[14px] font-bold text-white">Back to membership</button></>}
        {state === "error" && <><span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-red-100 text-red-600"><AlertTriangle size={26} /></span><h1 className="mt-4 font-head text-xl font-extrabold text-slate-900">We couldn't confirm this payment</h1><p className="mt-1 text-[13px] text-slate-500">No charge may have gone through. Please try again from your membership page.</p><button onClick={() => navigate("/billing")} className="mt-5 rounded-xl bg-[#080d1a] px-5 py-2.5 font-head text-[14px] font-bold text-white">Back to membership</button></>}
      </div>
    </div>
  );
}
